import {ArrowLeftOutlined, FileTextOutlined, IdcardOutlined, MailOutlined, MedicineBoxOutlined, PhoneOutlined} from '@ant-design/icons';
import {Avatar, Button, Card, Space, Tabs, Tag} from 'antd';
import PropTypes from 'prop-types';
import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import {RqUserLookupAllRole} from '../../../ReactQuery/apis';
import {selectAuth} from '../../../Redux/selectors';

import {PatientHistory} from './PatientHistory';
import {PrescriptionScreen} from './prescriptionScreen';

const BRAND = 'linear-gradient(135deg, #b69c66, #b69c66)';

const emptyText = value => (value || value === 0 ? value : <span className={'text-gray-400'}>—</span>);

const TagList = ({items, color = 'blue', max = 3}) => {
  if (!items?.length) {return <span className={'text-gray-400'}>—</span>;}
  const shown = items.slice(0, max);
  const extra = items.length - shown.length;

  return (
    <Space size={4} wrap>
      {shown.map(item => <Tag key={item} color={color} className={'m-0'}>{item}</Tag>)}
      {extra > 0 && <Tag className={'m-0'}>+{extra} more</Tag>}
    </Space>
  );
};

TagList.propTypes = {color: PropTypes.string, items: PropTypes.array, max: PropTypes.number};

// ─── Patient Info Header ───────────────────────────────────────────────────────
const PatientInfoHeader = ({patient, onBack}) => {
  const initials = (patient?.fullName || '?')
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div>
      <div className={'w-full bg-transparent flex justify-start'}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          className={' shrink-0 border-none bg-transparent hover:!bg-transparent text-primary-900 font-semibold hover:!border-none hover:!text-primary-800 justify-start'}
        >
            Back
        </Button>
      </div>
      <Card className={'rounded-xl border border-primary-100 shadow-sm mb-6'} bodyStyle={{padding: 20}}>
      
        <div className={'flex md:flex-row flex-col items-start justify-between flex-wrap gap-4'}>
          <div className={'flex items-center gap-4'}>
         
            <Avatar
              className={'md:flex hidden'}
              size={45}
              style={{background: BRAND, fontWeight: 700, fontSize: 14}}
            >
              {initials}
            </Avatar>
            <div>
              <div className={'flex items-center w-full md:gap-4 gap-2 flex-wrap'}>
                <Avatar
                  className={'md:hidden flex'}
                  size={45}
                  style={{background: BRAND, fontWeight: 700, fontSize: 14}}
                >
                  {initials}
                </Avatar>
                <h1 className={'!mb-0 text-primary-800 font-semibold text-lg'}>{patient?.fullName || 'Patient'}</h1>
                <Tag color={patient?.status === 'ACTIVE' ? 'green' : 'red'}>{patient?.status || '—'}</Tag>
                {patient?.bloodGroup && <Tag color={'volcano'}>{patient.bloodGroup}</Tag>}

                {/* <div className={'flex w-full justify-end'}>
                  <p>
                  Patient Since : {patient?.createdOn ? new Date(patient.createdOn).toLocaleDateString() : '—'}
                  </p>
                </div> */}
                
              </div>
              <Space size={16} wrap className={'text-slate-600 text-sm mt-1'}>
                <span className={'flex items-center gap-1'}><IdcardOutlined /> {patient?.age ? `${patient.age} yrs` : '—'} | {patient?.gender || '—'}</span>
                <span className={'flex items-center gap-1'}><PhoneOutlined /> {patient?.mobileNumber || '—'}</span>
                <span className={'flex items-center gap-1'}><MailOutlined /> {patient?.email || '—'}</span>
              </Space>
            </div>
          </div>
        </div>

        <div className={'grid md:grid-cols-4 grid-cols-1 justify-start gap-2 mt-2'}>
          <p className={'col-span-1 border p-1 border-primary-50 rounded-md text-slate-400'}>Occupation <span className={'text-slate-700 flex flex-col'}>{emptyText(patient?.occupation)}</span></p>
          <p className={'col-span-1 border p-1 border-primary-50 rounded-md text-slate-400'}>Medical Name <span className={'text-slate-700 flex flex-col'}>{emptyText(patient?.medicalname)}</span> </p>
          <p className={'md:col-span-2 border p-1 border-primary-50 rounded-md text-slate-400'}>Address <span className={'text-slate-700 flex flex-col'}> {emptyText(patient?.address)}</span></p>

        </div>
      
      </Card>
    </div>
  );
};

PatientInfoHeader.propTypes = {
  onBack: PropTypes.func.isRequired,
  patient: PropTypes.object,
};

const PatientDetailsScreen = () => {
  const {id: patientId} = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;

  const {data: allPatients = []} = RqUserLookupAllRole('PATIENT', token);

  const patient = useMemo(
    () => allPatients.find(p => String(p.id) === String(patientId)) || location.state?.patient,
    [allPatients, patientId, location.state],
  );

  const handleBack = () => navigate(-1);

  return (
    <div className={'min-h-screen md:p-6 p-2'}>
      <PatientInfoHeader patient={patient} onBack={handleBack} />

      <div className={' bg-transparent '}>
        <Tabs
          defaultActiveKey={'history'}
          className={'custom-tabs'}
          items={[
            {
              key: 'history',
              label: (
                <span className={'flex items-center gap-2 px-1'}>
                  <FileTextOutlined /> Medical History
                </span>
              ),
              children: <PatientHistory patientId={patientId} patientName={patient?.fullName} token={token} />,
            },
            {
              key: 'prescriptions',
              label: (
                <span className={'flex items-center gap-2 px-1'}>
                  <MedicineBoxOutlined /> Prescriptions
                </span>
              ),
              children: <PrescriptionScreen patient={patient} patientId={patientId} token={token} />,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default PatientDetailsScreen;
